/* eslint-disable max-lines */
import { handleActions, createAction } from "redux-actions";
import config from "appConfig";
import moment from "moment";
import {
  api,
  formatDate,
  notification,
  parseAdDetailValue,
  multiLevelRouteField,
  checkEmptyObject,
  removeProperties,
  stringifyURI,
  checkEmptyArray,
  dispatchPermutiveEvent
} from "../../services";
import {
  AdsSortOption,
  DetailGroupType,
  DetailValueType,
  AdStatus,
  AdDetailFormat,
  PageRoute,
  ModuleFormat,
  DataSource,
  CategoryName,
  ModuleNameHr,
  QueryParam,
  AdAutoPromotionStatus,
  PermutiveEvents,
  emptyUUID,
  ModuleName
} from "../../models";
import { getDetailConfig, getSummaryConfig } from "../../dataConfig";
import { getCategoryConfig } from "../../dataConfig/categoryConfig";
import history, {
  navigateTo404Page,
  redirectFromForbiddenAd
} from "../../history";
import {
  adInterface,
  AdStatisticsInterface,
  rootReducerInterface,
  subLevelInterface,
  UserI
} from "../../typings/interfaces";
import {
  AdDetailDataI,
  AdDetailI,
  AdListI,
  AdStatisticI,
  AttributeGroupsI,
  AttributeI,
  BuildSubLevelPayloadI,
  CategoriesI,
  ConfigGroupI,
  EmailDataI,
  ModulesI,
  ParseAdDetailLevelI,
  PassedAdI,
  PopularCategoriesI,
  PopularModulesI,
  PopularsI,
  ReportAdDataI,
  RouteLinksI,
  SpecialSectionConfigsI,
  SubLevelResourceChildrenI,
  UserStatisticI
} from "../../typings/routesTypes";
import {
  mapCategoryToEn,
  mapCategoryToHr,
  mapModuleToEn,
  mapModuleToHr
} from "../LanguageMapper";
import { MultiLevelFields } from "../../models/enums";
import { makesToNodes } from "../Configuration/helpers";

const AD_NEW_GET = "AD_NEW_GET";
const AD_NEW_CLEAR = "AD_NEW_CLEAR";
const AD_DISCOUNTED_GET = "AD_DISCOUNTED_GET";
const AD_DISCOUNTED_CLEAR = "AD_DISCOUNTED_CLEAR";
const AD_DETAILS_GET = "AD_DETAILS_GET";
const UPDATE_VIEW_COUNT = "UPDATE_VIEW_COUNT";
const AD_SMARTLINK_GET = "AD_SMARTLINK_GET";
const AD_STATISTICS_GET = "AD_STATISTICS_GET";
const AD_CAROUSEL_SCROLL_TRACK = "AD_CAROUSEL_SCROLL_TRACK";
const AD_SECTION_CONFIGS_GET = "AD_SECTION_CONFIGS_GET";
const AD_DETAILS_SEND_MAIL_TO_DEALER = "AD_DETAILS_SEND_MAIL_TO_DEALER";
const AD_MY_ADS_GET = "AD_MY_ADS_GET";
const AD_MY_ADS_STATISTIC = "AD_MY_ADS_STATISTIC";

const AD_ENABLE_AUTO_PROMOTION = "AD_ENABLE_AUTO_PROMOTION";
const AD_DISABLE_AUTO_PROMOTION = "AD_DISABLE_AUTO_PROMOTION";

const AD_MY_ADS_DELETE = "AD_MY_ADS_DELETE";
const AD_SET_FORBID_ADS = "AD_SET_FORBID_ADS";
const AD_SET_AGE_VERIFIED = "AD_SET_AGE_VERIFIED";
const AD_MY_ADS_ACTIVE = "AD_MY_ADS_ACTIVE";
const AD_MY_ADS_RENEW = "AD_MY_ADS_RENEW";
const AD_MY_ADS_PAUSED = "AD_MY_ADS_PAUSED";
const AD_MY_ADS_SOLD = "AD_MY_ADS_SOLD";
const AD_MY_ADS_PRICE_CHANGE = "AD_MY_ADS_PRICE_CHANGE";
const USER_GET_INFO = "USER_GET_INFO";
const AD_REPORT = "AD_REPORT";
const SEARCH_SUGGESTION_GET = "SEARCH_SUGGESTION_GET";
const SEARCH_TEXT_SET = "SEARCH_TEXT_SET";
const POPULAR_GET = "POPULAR_GET";
const SIMILAR_GET = "SIMILAR_GET";
const STATISTIC_GET = "STATISTIC_GET";
const SUB_LEVEL_GET = "SUB_LEVEL_GET";
const SUB_LEVEL_SEARCH_UPDATE = "SUB_LEVEL_SEARCH_UPDATE";
const SUB_LEVEL_SEARCH_DELETE = "SUB_LEVEL_SEARCH_DELETE";
const SELLER_MODULE_GET = "index/seller/get-modules";
const SELLER_DIRECT_CONTACT = "index/seller/direct-contact-seller";
const AD_INSURANCE_DATA = "AD_INSURANCE_DATA";

const localPopulars = [
  {
    displayName: "Automobili",
    value: CategoryName.car,
    gradientFrom: "#E36073",
    gradientTo: "#E57369"
  },
  {
    displayName: null,
    value: CategoryName.flatsForSale,
    gradientFrom: "#17B1C6",
    gradientTo: "#9DD3D7"
  },
  {
    displayName: null,
    value: CategoryName.housesForSale,
    gradientFrom: "#F8961E",
    gradientTo: "#EBB986"
  },
  {
    displayName: null,
    value: ModuleName.machinesAndTools,
    gradientFrom: "#6A4878",
    gradientTo: "#BA9EB8"
  },
  {
    displayName: null,
    value: ModuleName.nautical,
    gradientFrom: "#1791C6",
    gradientTo: "#9DC9D7"
  },
  {
    displayName: null,
    value: ModuleName.sportsAndEquipments,
    gradientFrom: "#43AA8B",
    gradientTo: "#90BE6D"
  }
];

const navigateToSearchCategory = (
  module: string,
  params: any,
  levelName?: string | null
) => {
  const { category } = params;

  history.replace(
    `/${module}${category ? `/${mapCategoryToHr(category, module)}` : ""}${levelName ? `/${levelName}` : ""
    }${PageRoute.search}${stringifyURI(params, QueryParam.searchQuery)}`
  );

  return null;
};

// Dataset behind a category landing page (sub level pages): the lv1/lv2
// subcategory tree when the category has one, otherwise its makes/models.
const ignoreError = () => [];

const loadSubLevelSource = async (module: string, category: string) => {
  const moduleEn = mapModuleToEn(module);
  const categoryEn = mapCategoryToEn(category, module);

  const subcategories = await api.Configuration.subcategories(
    moduleEn,
    categoryEn,
    ignoreError
  );

  if (checkEmptyArray(subcategories)) {
    return {
      dataSource: DataSource.others,
      source: subcategories,
      ...MultiLevelFields.subcategories
    };
  }

  const makes = await api.Configuration.makes(categoryEn, ignoreError);

  if (checkEmptyArray(makes)) {
    return {
      dataSource: DataSource.make,
      source: makesToNodes(makes),
      ...MultiLevelFields.make
    };
  }

  return null;
};

const getRouteLinks = async (ad: PassedAdI) => {
  const modules = await api.Module.list();
  const moduleName = modules.find((m: ModulesI) => m.name === ad.module)
    .displayName;
  const moduleHr = modules.find((m: ModulesI) => m.name === ad.module).route;

  const categories = await api.Category.list();
  const categoryName = categories.find(
    (c: CategoriesI) => c.name === ad.category
  ).displayName;
  const categoryHr = categories.find((c: CategoriesI) => c.name === ad.category)
    .route;

  if (moduleHr == ModuleNameHr.realEstate) {
    return [
      {
        title: moduleName,
        link: `/${moduleHr}/prodaja-kuca${stringifyURI({
          category: "houses-for-sale"
        }, QueryParam.searchQuery)}`
      },
      {
        title: categoryName,
        link: `/${moduleHr}/${categoryHr}${stringifyURI({
          category: categoryHr
        }, QueryParam.searchQuery)}`
      }
    ];
  } else {
    return [
      {
        title: moduleName,
        link: `/${moduleHr}`
      },
      {
        title: categoryName,
        link: `/${moduleHr}/${categoryHr}`
      }
    ];
  }
};

const addExpireSummary = (
  ad: PassedAdI,
  today: moment.Moment,
  summary: string[]
) => {
  const expireDate = moment(ad.expirationDate).startOf("day");
  const remainingDays = expireDate.diff(today, "days");
  const remainingYears = expireDate.diff(today, "year");

  let value: string;

  if (remainingYears > 5) {
    // Dont show anything if ad expiration date is bigger than 5 years
    return
  }

  if (remainingDays == 0) {
    value = `${"danas"}`;
  }
  else {
    value = `${remainingDays} ${remainingDays === 1
      ? "dan"
      : "dana"
      }`;
  }

  summary.unshift(`${"Do isteka"} ${value}`);
};

// eslint-disable-next-line max-lines-per-function
// Url slug path of a multi level ad value: "bmw/x5" for modelId, "bmw" for makeId,
// "strucna/informatika" for categoryLv2Id. The routes come with the ad detail.
const buildDetailMultiLevelRoute = (ad: PassedAdI, field: string) => {
  const chain = [MultiLevelFields.make.fields, MultiLevelFields.subcategories.fields].find(
    fields => fields.includes(field)
  );

  if (!chain) {
    return null;
  }

  const routes = chain
    .slice(0, chain.indexOf(field) + 1)
    .map(f => ad[multiLevelRouteField(f)])
    .filter(Boolean);

  return routes.length > 0 ? routes.join("/") : null;
};

// Breadcrumb order is module > category > subcategories > make > model, whatever the order of the
// detail attributes in the config; links without a field (module, category) stay in front.
const orderRouteLinks = (routeLinks: RouteLinksI[]) => {
  const rank = (link: RouteLinksI) => {
    if (!link.field) return 0;
    if (MultiLevelFields.subcategories.fields.includes(link.field)) return 1;
    if (MultiLevelFields.make.fields.includes(link.field)) return 2;

    return 3;
  };

  return routeLinks
    .map((link, index) => ({ link, index }))
    .sort((a, b) => rank(a.link) - rank(b.link) || a.index - b.index)
    .map((entry) => entry.link);
};

const parseDetailsData = async (
  ad: PassedAdI,
  favoriteIds: string[],
  routeLinks: RouteLinksI[]
) => {
  const details = [];
  const detailConfig = await getDetailConfig(ad.module);

  const configGroups = getCategoryConfig(detailConfig, ad.category).groups;
  const specialSectionConfigs = getCategoryConfig(detailConfig, ad.category)
    .section;

  let specialAttributes = [];

  if (specialSectionConfigs) {
    specialAttributes = specialSectionConfigs
      .map((attribute: SpecialSectionConfigsI) => ({
        field: attribute.field,
        title: attribute.title,
        iconRelativeUrl: attribute.iconRelativeUrl,
        label: attribute.label,
        tooltip: attribute.tooltip,
        value: parseAdDetailValue(
          attribute,
          ad[attribute.field],
          DetailGroupType.valueWithTitle,
          ad
        )
      }))
      .filter((attribute: SpecialSectionConfigsI) => attribute.value);
  }


  // eslint-disable-next-line complexity,max-lines-per-function
  configGroups.forEach((group: ConfigGroupI) => {
    let groupData = [];

    if (group.type === DetailGroupType.map) {
      const coordinate = {
        lat: ad[group.latitudeField],
        lng: ad[group.longitudeField]
      };

      if (coordinate.lat && coordinate.lng) {
        groupData.push(coordinate);
      }
    } else {
      const attributes = [];

      // Flat all groups of valueOnly type, but keep the group name to re-group it later
      if (group.type === DetailGroupType.valueOnly) {
        group.attributeGroups.forEach((ag: AttributeGroupsI) => {
          attributes.push(
            // eslint-disable-next-line max-nested-callbacks
            ...ag.attributes.map((attribute: AttributeI) => ({
              ...attribute,
              group: ag.name
            }))
          );
        });
      } else {
        attributes.push(...group.attributes);
      }

      attributes.forEach(attribute => {
        const value = parseAdDetailValue(
          attribute,
          ad[attribute.field],
          group.type,
          ad
        );

        if (value) {
          interface resultInterface {
            title?: string;
            tooltip?: string;
            value?: string;
          }

          let result: resultInterface;

          // We need to append tyres dimensions
          if (attribute.children) {
            const dimensions = [];
            attribute.children.forEach(child => {
              let val = ad[child.field];

              // If the value is not array, we need to convert it to array, legacy data
              if (!Array.isArray(val)) {
                val = [val];
              }

              // Loop through the value and get the title of each value, else we will use "x" as the title
              const data = val.map(v => {
                let title = "";
                if (v) {
                  child.options.forEach(option => {
                    if (option.value == v) {
                      title = option.title;
                    }
                  });
                } else {
                  title = "x";
                }

                return title;
              });

              // Group the data by index for each dimension
              data.forEach((d, index) => {
                if (!dimensions[index]) dimensions[index] = [];
                dimensions[index].push(d);
              });
            });

            // Loop through the dimensions and convert it to string
            result = {
              title:
                attribute.title +
                `<span class="tyresData">` +
                dimensions
                  .filter(dimension => !dimension.every(d => d === "x"))
                  .map(dimension => `(${dimension[0]}/${dimension[1]} R${dimension[2]})`)
                  .join(", ") +
                `</span>`,
              value:
                group.type === DetailGroupType.valueOnly
                  ? attribute.group
                  : value,
              tooltip: attribute.tooltip // ✅ include tooltip
            };
          } else {
            result = {
              title: attribute.title,
              value:
                group.type === DetailGroupType.valueOnly
                  ? attribute.group
                  : value,
              tooltip: attribute.tooltip // ✅ include tooltip
            };
          }

          // Multi level values (make/model, subcategories) link to the search of that value
          if (attribute.type === DetailValueType.multiLevel) {
            const searchQuery: any = {
              module: mapModuleToHr(ad.module),
              category: mapCategoryToHr(ad.category, mapModuleToHr(ad.module)),
            };

            if (ad.categoryLv1Id && ad.categoryLv1Id !== emptyUUID) {
              searchQuery.categoryLv1Ids = [ad.categoryLv1Id];
            }
            if (ad.categoryLv2Id && ad.categoryLv2Id !== emptyUUID) {
              searchQuery.categoryLv2Ids = [ad.categoryLv2Id];
            }

            searchQuery[attribute.searchField] = [ad[attribute.field]];

            const multiLevelRoute = buildDetailMultiLevelRoute(ad, attribute.field);

            routeLinks.push({
              title: value,
              field: attribute.field,
              link: `/${mapModuleToHr(ad.module)}/${mapCategoryToHr(
                ad.category,
                ad.module
              )}${multiLevelRoute ? `/${multiLevelRoute}` : ""}${PageRoute.search}${stringifyURI(searchQuery, QueryParam.searchQuery)}`
            });
          }

          groupData.push(result);
        }
      });
    }

    if (groupData.length > 0) {
      // Re-grouping the group of valueOnly type
      if (group.type === DetailGroupType.valueOnly) {
        const valueOnlyData = [];

        groupData.forEach(data => {
          // eslint-disable-next-line max-nested-callbacks
          const item = valueOnlyData.find(x => x.group === data.value);
          if (item) {
            item.data.push(data.title);
          } else {
            valueOnlyData.push({
              group: data.value,
              data: [data.title]
            });
          }
        });

        groupData = valueOnlyData;
      }

      details.push({
        name: group.name,
        type: group.type,
        data: groupData
      });
    }
  });

  return {
    module: mapModuleToHr(ad.module),
    category: mapCategoryToHr(ad.category, ad.module),
    categoryLv1Id: ad.categoryLv1Id != emptyUUID ? ad.categoryLv1Id : null,
    categoryLv2Id: ad.categoryLv2Id != emptyUUID ? ad.categoryLv2Id : null,
    specialAttributes,
    summary: {
      id: ad.id,
      code: ad.code,
      externalId: ad.externalId,
      smartLink: ad.smartLink,
      status: ad.status,
      title: ad.title,
      creatorId: ad.creatorId,
      creatorEmail: ad.creatorEmail,
      description: ad.description,
      postedTime: ad.postedTime
        ? formatDate(ad.postedTime, config.longTimeFormat)
        : null,
      renewalTime: ad.renewalTime
        ? formatDate(ad.renewalTime, config.longTimeFormat)
        : null,
      images: ad.images || [],
      floorPlans: ad.floorPlans || [],
      isFavorite: Boolean(favoriteIds && favoriteIds.includes(ad.id)),
      isPromoted: ad.isPromoted,
      price: ad.price,
      priceTo: ad.priceTo,
      priceM2: ad.priceM2,
      averagePriceM2: ad.averagePriceM2,
      lowerRangePriceM2: ad.lowerRangePriceM2,
      higherRangePriceM2: ad.higherRangePriceM2,
      latitude: ad.latitude,
      rentPriceType: ad.rentPriceType,
      priceType: ad.priceType,
      longitude: ad.longitude,
      isPreciseLocation: ad.isPreciseLocation,
      hasPhones: ad.hasPhones,
      previousPrice: ad.previousPrice,
      priceReductionPercentage: ad.priceReductionPercentage,
      featuredProperty: ad.featuredProperty,
      videoUrl: ad.videoUrl,
      viewCount: ad.viewCount,
      searchedViewCount: ad.searchedViewCount,
      favoriteCount: ad.favoriteCount,
      externalUrl: ad.externalUrl,
      is18Old: ad.is18Old,
      countryId: ad.countryId,
      countyId: ad.countyId,
      cityId: ad.cityId,
      settlementId: ad.settlementId,
      module: ad.module,
      category: ad.category,
      permutiveData: ad.permutiveData,
      makeId: ad.makeId,
      modelId: ad.modelId,
      makeName: ad.makeName || null,
      makeRoute: ad.makeRoute || null,
      modelName: ad.modelName || null,
      modelRoute: ad.modelRoute || null,
      categoryLv1Name: ad.categoryLv1Name || null,
      categoryLv1Route: ad.categoryLv1Route || null,
      categoryLv2Name: ad.categoryLv2Name || null,
      categoryLv2Route: ad.categoryLv2Route || null,
      countryName: ad.countryName || null,
      countyName: ad.countyName || null,
      cityName: ad.cityName || null,
      settlementName: ad.settlementName || null,
      locationName: ad.locationName || null,
      // V2OGLASI-2648: locationScope 1=exact (default), 2=whole Croatia,
      // 3=picked counties, 4=picked cities. locationAreas carries display
      // names for the picked areas (empty for 1 and 2); locationAreaIds
      // is only needed by the edit form.
      locationScope: ad.locationScope || 1,
      locationAreas: ad.locationAreas || [],
      locationAreaIds: ad.locationAreaIds || [],
      settlementLatitude: ad.settlementLatitude || null,
      settlementLongitude: ad.settlementLongitude || null,
      makeYear: ad.makeYear,
      infoPage: ad.infoPage || null,
    },
    details,
    routeLinks: orderRouteLinks(routeLinks),
    sharingConfiguration: ad.sharingConfiguration,
    redirectData: ad.redirectData
  };
};

const handleGetAdDetailsError = async (
  module: string,
  response: { status: number }
) => {
  switch (response.status) {
    case 401:
      api.handleUnauthorizedError();
      break;

    case 400:
      navigateTo404Page(response);
      break;

    case 404:
      notification.showError("Oglas nije dostupan");
      navigateTo404Page(response);
      break;

    default:
      await api.handleCommonError(response);
      history.replace(`/${module}`);
      break;
  }

  throw response;
};

const handleGetSellerError = async (response: { status: number }) => {
  switch (response.status) {
    case 401:
      api.handleUnauthorizedError();
      break;

    case 404:
      notification.showError("Korisnik nije pronađen.");
      navigateTo404Page();
      break;

    default:
      await api.handleCommonError(response);
      break;
  }

  throw response;
};

// Actions
export const getNewAds = createAction(
  AD_NEW_GET,
  async ({
    module,
    favoriteIds = [],
    itemPerPage = config.maxAdPerPage,
    page,
    queryParams
  }) => {
    try {
      const queries = {
        ...queryParams,
        module,
        itemPerPage,
        page
      };
      const adList = await api.Ads.widgetSearch(queries);
      const data = await Promise.all(
        adList.data.map(async (ad: PassedAdI) => {
          const summaryConfig = await getSummaryConfig(ad.module);

          return api.parseSummaryData(summaryConfig, ad, favoriteIds);
        })
      );

      return {
        count: adList.count,
        page: adList.currentPage,
        nextPage: adList.nextPage,
        data
      };
    } catch (error) {
      return null;
    }
  }
);

export const clearNewAds = createAction(
  AD_NEW_CLEAR,
  () => null
);

export const setForbidAds = createAction(
  AD_SET_FORBID_ADS,
  (forbid: boolean) => forbid
);

export const setAgeVerified = createAction(
  AD_SET_AGE_VERIFIED,
  (isVerified: boolean) => isVerified
);

export const getDiscountedAds = createAction(
  AD_DISCOUNTED_GET,
  async ({
    module,
    favoriteIds = [],
    itemPerPage,
    page,
    queryParams
  }) => {
    try {
      const queries = {
        ...queryParams,
        module,
        withReducedPrice: true,
        itemPerPage,
        page
      };

      const adList = await api.Ads.widgetSearch(queries);
      const data = await Promise.all(
        adList.data.map(async (ad: PassedAdI) => {
          const summaryConfig = await getSummaryConfig(ad.module);

          return api.parseSummaryData(summaryConfig, ad, favoriteIds);
        })
      );

      return {
        count: adList.count,
        page: adList.currentPage,
        nextPage: adList.nextPage,
        data
      };
    } catch (error) {
      return null;
    }
  }
);

export const clearDiscountedAds = createAction(
  AD_DISCOUNTED_CLEAR,
  () => null
);

export const getAdSmartlink = createAction(
  AD_SMARTLINK_GET,
  async (adCode: string) => {
    try {
      const ads = await api.Ads.list({
        code: adCode,
        format: AdDetailFormat.all
      });

      const ad = ads.data[0];

      return {
        module: ad.module,
        link: `${ad.smartLink}/${ad.code || ad.id}`
      };
    } catch (error) {
      return {};
    }
  }
);

export const getAdDetails = createAction(
  AD_DETAILS_GET,
  async (
    module: string,
    code: string,
    getCreatorInfo: Function,
    favoriteIds: string[],
    user: UserI | Object | null,
    callback?: () => void
  ) => {
    try {
      const ads = await api.Ads.single(
        { code, format: AdDetailFormat.all },
        handleGetAdDetailsError.bind(this, module)
      );

      if (ads.count === 0 || ads.data.length == 0) {
        const { module, category, status } = ads.redirectData;
        redirectFromForbiddenAd(
          mapModuleToHr(module),
          mapCategoryToHr(category, module)
        );

        switch (status) {
          case AdStatus.deleted:
          case AdStatus.paused:
          case AdStatus.blocked:
          case AdStatus.expired:
            notification.show(
              "Ovaj oglas više nije dostupan."
            );
            break;
        }

        callback && callback();
        return {};
      }

      const ad = {
        ...ads.data[0],
        redirectData: ads.redirectData
      };

      const routeLinks = await getRouteLinks(ad);

      getCreatorInfo(ad.creatorId);

      // Dispatch permutive event
      (user !== null && user !== undefined) && dispatchPermutiveEvent(
        PermutiveEvents.AdPreview,
        user,
        ad
      );

      callback && callback();

      return await parseDetailsData(ad, favoriteIds, routeLinks);
    } catch (error) {
      callback && callback();
      return null;
    }
  }
);

export const updateViewCount = createAction(
  UPDATE_VIEW_COUNT,
  async (id: string, callback: Function) => {
    try {
      await api.Ads.updateViewCount(id);
      callback && callback();
    } catch (error) {
      callback && callback();
    }
  }
);

export const getCreatorInfo = createAction(
  USER_GET_INFO,
  async (userId: string) => {
    try {
      return await api.Ads.seller(userId, handleGetSellerError);
    } catch (error) {
      return {};
    }
  }
);

// Track which ad's carousel has been pinged for the carousel-scroll endpoint this session
export const trackCarouselScroll = createAction(
  AD_CAROUSEL_SCROLL_TRACK,
  (adId: string) => adId
);

// Get ad statistics
export const getAdStatistics = createAction(
  AD_STATISTICS_GET,
  async (
    adId: string,
    callback?: (adStatistics: AdStatisticsInterface | Boolean) => void
  ) => {
    try {
      const adStatistics = await api.Ads.adStatistics(
        adId
      );

      if (!checkEmptyObject(adStatistics)) {
        callback && callback(false);
        return null;
      }

      callback && callback(adStatistics);
      return adStatistics;
    } catch (error) {
      callback && callback(false);
      return null;
    }
  }
);

export const sendEmailToDealerAction = createAction(
  AD_DETAILS_SEND_MAIL_TO_DEALER,
  async (data: EmailDataI, callback: Function) => {
    try {
      await api.Ads.contactDealer(data);
      notification.show("Vaša je poruka poslana!");

      callback && callback(true);
    } catch (error) {
      callback && callback(false);
    }
  }
);

export const directContactSellerAction = createAction(
  SELLER_DIRECT_CONTACT,
  async ({ id, data }, callback: Function) => {
    try {
      await api.User.email(id, data);
      notification.show("Vaša je poruka poslana!");

      callback && callback(true);
    } catch (e) {
      callback && callback(false);
    }
  }
);

export const getMyAds = createAction(
  AD_MY_ADS_GET,
  async (
    page: number | undefined,
    searchCriteria: { status: number },
    actionTime: number
  ) => {
    try {
      const queries = {
        ...searchCriteria,
        status: (searchCriteria && searchCriteria.status) || undefined,
        page,
        itemPerPage: config.maxAdPerPage
      };

      const adList = await api.Ads.myAds(queries);

      const today = moment().startOf("day");

      return {
        count: adList.count,
        page: adList.currentPage,
        nextPage: adList.nextPage,
        actionTime,
        data: adList.data.map((ad: PassedAdI) => {
          const summary = [`${"Kod"} ${ad.code}`];

          if (
            ad.status === AdStatus.active ||
            ad.status === AdStatus.paused
          ) {
            addExpireSummary(ad, today, summary);
          }

          return api.parseSummaryData(null, ad, null, summary);
        })
      };
    } catch (error) {
      return null;
    }
  }
);

export const getMyAdsStatistic = createAction(AD_MY_ADS_STATISTIC, async () => {
  try {
    const data = await api.Ads.myAdsStatistic();

    return {
      ...data,
      0: Object.values(data).reduce((a: number, b: number) => a + b, 0)
    };
  } catch (error) {
    return null;
  }
});


export const updateMyAds = createAction(AD_MY_ADS_GET, (myAds: any) => myAds);

export const myAdsDeleteAdAction = createAction(
  AD_MY_ADS_DELETE,
  async (adId: string, adList: AdListI, callback: Function) => {
    try {
      await api.Ads.deleteAds(adId);
      callback(adId);

      return {
        ...adList,
        data: adList.data.filter(x => x.id !== adId)
      };
    } catch (error) {
      callback(false);

      return null;
    }
  }
);

export const reportAd = createAction(
  AD_REPORT,
  async (data: ReportAdDataI, callback: Function) => {
    try {
      await api.Ads.report(data);

      notification.show("Prijava je uspješna");

      callback && callback(true);

      return null;
    } catch (error) {
      callback && callback(false);

      return null;
    }
  }
);

export const activeAd = createAction(
  AD_MY_ADS_ACTIVE,
  async (adId: string, adList: AdListI, callback: Function) => {
    try {
      await api.Ads.active(adId);

      const currentAd = adList.data.find(ad => ad.id === adId);
      currentAd.status = AdStatus.active;
      callback(true);

      return adList;
    } catch (error) {
      callback(false);

      return null;
    }
  }
);

export const renewAd = createAction(
  AD_MY_ADS_RENEW,
  async (adId: string, callback?: Function) => {
    try {
      const response = await api.Ads.renew(adId);
      callback && callback(response);

    } catch (error) {
      callback && callback();
      return null;
    }
  }
);

export const pausedAd = createAction(
  AD_MY_ADS_PAUSED,
  async (adId: string, adList: AdListI, callback: Function) => {
    try {
      await api.Ads.pause(adId);

      const currentAd = adList.data.find(ad => ad.id === adId);
      currentAd.status = AdStatus.paused;
      callback(true);

      return adList;
    } catch (error) {
      callback(false);

      return null;
    }
  }
);

export const soldAd = createAction(
  AD_MY_ADS_SOLD,
  async (adId: string, adList: AdListI, callback: Function) => {
    try {
      await api.Ads.sold(adId);

      if (adList && adList.data) {
        const currentAd = adList.data.find(ad => ad.id === adId);
        currentAd.status = AdStatus.sold;
        callback && callback(true);

        return adList;
      } else {
        callback && callback(true);

        return;
      }

    } catch (error) {
      callback && callback(false);
      return null;
    }
  }
);

export const changeAdPrice = createAction(
  AD_MY_ADS_PRICE_CHANGE,
  async (adId: string, priceData: { price: number; priceTo?: number }, adList: AdListI, callback: Function) => {
    try {
      await api.Ads.updatePrice(adId, priceData);

      if (adList && adList.data) {
        const currentAd = adList.data.find(ad => ad.id === adId);
        currentAd.price = priceData.price;
        if (priceData.priceTo !== undefined) {
          currentAd.priceTo = priceData.priceTo;
        }
        callback && callback(true);

        return adList;
      } else {
        callback && callback(true);

        return;
      }
    } catch (error) {
      callback && callback(false);
      return null;
    }
  }
);

export const getSuggestions = createAction(
  SEARCH_SUGGESTION_GET,
  async (text: string, actionTime?: number) => {
    try {
      if (text.length <= 2) {
        return {
          suggestions: { categories: [], hits: 0, notFound: false },
          enhancedSuggestions: { textSearchResults: undefined, categoryMatchSuggestions: [] },
          alternatives: {},
          actionTime
        };
      }

      const response = await api.Search.suggestion({ text });

      // Handle new response structure with textSearchResults and categoryMatchSuggestions
      let suggestions = response.textSearchResults || response;
      let enhancedSuggestions = {
        textSearchResults: response.textSearchResults,
        categoryMatchSuggestions: response.categoryMatchSuggestions || []
      };

      let alternatives: string[];
      if (suggestions.notFound) {
        alternatives = await api.Search.suggestionAlternative({ text });
      }

      return { suggestions, enhancedSuggestions, alternatives, actionTime };
    } catch (error) {
      return {
        suggestions: { categories: [], hits: 0, module: "", notFound: false },
        enhancedSuggestions: { textSearchResults: undefined, categoryMatchSuggestions: [] },
        alternatives: {},
        actionTime
      };
    }
  }
);

export const setSearchText = createAction(
  SEARCH_TEXT_SET,
  (text: string) => text
);

export const getPopulars = createAction(
  POPULAR_GET,
  async (modules: PopularModulesI[], categories: PopularCategoriesI[]) => {
    try {
      if (modules.length === 0 || categories.length === 0) {
        return [];
      }
      const populars = await api.Ads.popular();

      populars.forEach((popular: PopularsI, index: number) => {
        const remotePopular = populars[index];

        const localPopular = localPopulars.find(
          c => c.value == popular.category || c.value == popular.module
        );

        if (localPopular) {
          remotePopular.displayName = localPopular.displayName || remotePopular.displayName;
          remotePopular.gradientFrom = localPopular.gradientFrom;
          remotePopular.gradientTo = localPopular.gradientTo;
        }
      });
      return populars;
    } catch (error) {
      return [];
    }
  }
);

export const getStatistics = createAction(STATISTIC_GET, async () => {
  try {
    let userStatistic: UserStatisticI;
    let adStatistic: AdStatisticI;
    await Promise.all([
      (userStatistic = await api.User.statistic()),
      (adStatistic = await api.Ads.statistic())
    ]);

    return {
      activeAdsCount: adStatistic.activeAdCount,
      newActiveAdsCount: adStatistic.newActiveAdCount,
      registeredUsersCount: userStatistic.registeredUserCount,
      tradesCount: userStatistic.tradeCount
    };
  } catch (error) {
    return {};
  }
});


export const getSimilarAds = createAction(
  SIMILAR_GET,
  async (
    id: string,
    favoriteIds: string[],
    quantity: number,
    callback?: () => void,
    module?: string,
    category?: string
  ) => {
    try {
      let similarAds;

      // If no adId but we have module/category, use widgetSearch instead
      if (!id && module && category) {
        const result = await api.Ads.widgetSearch({
          module: mapModuleToEn(module),
          category: mapCategoryToEn(category, module),
          itemPerPage: quantity
        });
        similarAds = result.data || [];
      } else if (id) {
        similarAds = await api.Ads.similar(id, quantity);
      } else {
        callback && callback();
        return [];
      }

      if (similarAds.length === 0) {
        callback && callback();
        return similarAds;
      }
      const summaryConfig = await getSummaryConfig(similarAds[0].module);

      callback && callback();
      return similarAds.map((ad: PassedAdI) =>
        api.parseSummaryData(summaryConfig, ad, favoriteIds)
      );
    } catch (e) {
      callback && callback();
      return [];
    }
  }
);

export const getSellerModuleAction = createAction(
  SELLER_MODULE_GET,
  async (id: string) => {
    try {
      return await api.Module.sellerModules(id);
    } catch (e) {
      return [];
    }
  }
);

export const getSubLevelCount = createAction(
  SUB_LEVEL_GET,
  // eslint-disable-next-line complexity,max-lines-per-function
  async (
    module: string,
    category: string,
    level: number,
    levelId: string,
    callback?: Function,
    sortNative?: boolean
  ) => {
    try {
      const resource = await loadSubLevelSource(module, category);

      if (!resource || !resource.fields[level]) {
        callback && callback();

        return navigateToSearchCategory(module, {
          category: category,
          module: module
        });
      }

      let { source } = resource;
      let levelName = "";
      let parentId = levelId;

      if (levelId) {
        // level1 url segment is the route of the lv1 node (legacy links may carry the id)
        const levelSource = resource.source.find(
          s => s.id === levelId || s.route == levelId
        );

        if (!levelSource) {
          callback && callback();

          return navigateToSearchCategory(module, {
            category: category,
            module: module
          });
        }

        levelName = levelSource.name;
        parentId = levelSource.id;
        source = levelSource.children;
      }

      if (!source || !checkEmptyArray(source)) {
        const id = resource.source.find(src => src.route == levelId);

        callback && callback();

        return navigateToSearchCategory(
          module,
          {
            category: category,
            [resource.searchFields[level - 1]]: [id ? id.id : levelId],
            module: module
          },
          id ? (id.route ? id.route : null) : null
        );
      }

      let parentCount = 0;
      if (level > 0) {
        const parentCounts = await api.Ads.count({
          module: mapModuleToEn(module),
          category: mapCategoryToEn(category, module),
          field: resource.fields[level - 1]
        });
        parentCount = parentCounts[parentId] || 0;
      }

      const counts = await api.Ads.count({
        module: mapModuleToEn(module),
        category: mapCategoryToEn(category, module),
        field: resource.fields[level]
      });

      const subLevel = source.map(({ children, models, ...r }) => ({
        ...r,
        dataSource: resource.dataSource,
        count: counts[r.id] || 0,
        hasChild: children && children.length > 0,
        checked: false
      }));

      const children = {};
      source.forEach(s => {
        if (s.children) {
          // eslint-disable-next-line max-nested-callbacks
          children[s.id] = s.children.map(
            (c: { id: string; name: string }) => c.id
          );
        }
      });

      callback && callback();

      return {
        children,
        subLevel: sortNative ? subLevel : subLevel.sort(
          (l1: subLevelInterface, l2: subLevelInterface) => l2.count - l1.count
        ),
        parentCount,
        levelName,
        dataSource: resource.dataSource,
        tree: resource.source,
        searchFields: resource.searchFields
      };
    } catch (e) {
      callback && callback();

      return null;
    }
  }
);

// levels: selected ids from lv1 down (e.g. [lv1Id] or [lv1Id, lv2Id])
export const updateSubLevelSearch = createAction(
  SUB_LEVEL_SEARCH_UPDATE,
  (levels: string[]) => ({ levels })
);

// Without levelId the whole sub level selection is reset
export const deleteSubLevelSearch = createAction(
  SUB_LEVEL_SEARCH_DELETE,
  (levelId?: string) => ({ levelId })
);

// Enable auto promotion
export const enableAdAutoPromotion = createAction(
  AD_ENABLE_AUTO_PROMOTION,
  async (
    adId: string,
    adList?: AdListI,
    callback?: (Boolean) => void
  ) => {
    try {
      await api.Ads.enableAutoPromotion(adId);

      if (adList) {
        const currentAd = adList.data.find(ad => ad.id === adId);
        currentAd.autoPromotionStatus = AdAutoPromotionStatus.disabled;
      }

      callback && callback(true);

      if (adList) {
        return adList;
      }
    } catch (error) {
      callback && callback(false);
      if (adList) {
        return null;
      }
    }
  }
);

// Disable auto promotion
export const disableAdAutoPromotion = createAction(
  AD_DISABLE_AUTO_PROMOTION,
  async (
    adId: string,
    adList?: AdListI,
    callback?: (Boolean) => void
  ) => {
    try {
      await api.Ads.disableAutoPromotion(adId);
      if (adList) {
        const currentAd = adList.data.find(ad => ad.id === adId);
        currentAd.autoPromotionStatus = AdAutoPromotionStatus.disabled;
      }

      callback && callback(true);

      if (adList) {
        return adList;
      }
    } catch (error) {
      callback && callback(false);
      if (adList) {
        return null;
      }
    }
  }
);


// Selectors
export const selectMyAds = (state: rootReducerInterface) => state.ad.myAds;
export const selectMyAdsStatistic = (state: rootReducerInterface) =>
  state.ad.myAdsStatistic;

export const selectAdDetails = (state: rootReducerInterface) =>
  state.ad.adDetails;

export const selectPopulars = (state: rootReducerInterface) =>
  state.ad.populars;

// Selectors
export const selectAdStatistics = (state: rootReducerInterface) =>
  state.ad.adStatistics;
export const selectTrackedCarouselAdIds = (state: rootReducerInterface) =>
  state.ad.trackedCarouselAdIds;
export const selectSuggestions = (state: rootReducerInterface) =>
  state.ad.suggestions;
export const selectSearchText = (state: rootReducerInterface) =>
  state.ad.searchText;
export const selectNewAds = (state: rootReducerInterface) => state.ad.newAds;
export const selectDiscountedAds = (state: rootReducerInterface) => state.ad.discountedAds;
export const selectSeller = (state: rootReducerInterface) =>
  state.ad.adDetails && state.ad.adDetails.creator;
export const selectSimilarAds = (state: rootReducerInterface) =>
  state.ad.similarAds;
export const selectSellerModules = (state: rootReducerInterface) =>
  state.global.modules.filter(module =>
    state.ad.seller.modules.includes(module.name)
  );
export const selectSubLevel = (state: rootReducerInterface) =>
  state.ad.subLevel;
export const selectSubLevelName = (state: rootReducerInterface) =>
  state.ad.subLevelName;
export const selectSubLevelSearch = (state: rootReducerInterface) =>
  state.ad.subLevelSearch;
export const selectMakeDatasource = (state: rootReducerInterface) =>
  state.ad.subLevelSource === DataSource.make;
export const selectSubLevelSource = (state: rootReducerInterface) =>
  state.ad.subLevelSource;
// lv1 nodes (with children) of the current sub level page
export const selectSubLevelTree = (state: rootReducerInterface) =>
  state.ad.subLevelTree;
export const selectSubLevelSearchFields = (state: rootReducerInterface) =>
  state.ad.subLevelSearchFields;
export const selectSubLevelParentCount = (state: rootReducerInterface) =>
  state.ad.subLevelParentCount;
export const selectForbidAds = (state: rootReducerInterface) =>
  state.ad.forbiddAds
export const selectIsAgeVerified = (state: rootReducerInterface) =>
  state.ad.isAgeVerified

// Reducer
const initialState = {
  adDetails: null,
  myAds: null,
  myAdsStatistic: {},
  suggestions: {
    suggestions: { categories: [], hits: 0, module: "", notFound: false },
    enhancedSuggestions: { textSearchResults: undefined, categoryMatchSuggestions: [] },
    alternatives: {}
  },
  newAds: {},
  discountedAds: {},
  populars: [],
  statistics: null,
  adStatistics: null,
  smartlinkData: {},
  similarAds: [],
  searchText: "",
  seller: {
    modules: []
  },
  subLevel: [],
  subLevelSearch: {},
  subLevelSource: null,
  subLevelTree: [],
  subLevelSearchFields: [],
  subLevelParentCount: 0,
  forbiddAds: false,
  isAgeVerified: null,
  trackedCarouselAdIds: []
};

// eslint-disable-next-line complexity,max-lines-per-function
// Current sub level dataset in the shape the search builders below expect
const currentSubLevelResource = (state: adInterface) => ({
  lvSearchField: state.subLevelSearchFields || [],
  children: state.subLevelTree || []
});

const setSubLevelSearch = (state: adInterface, { payload }) => {
  const resource = currentSubLevelResource(state);
  const subLevelSearch = {};
  const lastLevelIndex = payload.levels.length - 1;
  const lastLevelId = payload.levels[payload.levels.length - 1];
  const searchField = resource.lvSearchField[lastLevelIndex];

  // remove selected level ID if select the deepest level
  if (
    lastLevelIndex === resource.lvSearchField.length - 1 &&
    subLevelSearch[searchField] &&
    subLevelSearch[searchField].includes(lastLevelId)
  ) {
    // remove selected child id
    subLevelSearch[searchField] =
      subLevelSearch[searchField].length > 1
        ? subLevelSearch[searchField].filter((id: string) => id !== lastLevelId)
        : undefined;

    // add parents id if remove all child id
    const children = resource.children.find(
      (c: SubLevelResourceChildrenI) =>
        c.id === payload.levels[lastLevelIndex - 1]
    );
    if (
      !subLevelSearch[searchField] ||
      (children &&
        !children.children.find((c: { name: string; id: string }) =>
          subLevelSearch[searchField].includes(c.id)
        ))
    ) {
      const parentField = resource.lvSearchField[lastLevelIndex - 1];

      subLevelSearch[parentField] = !subLevelSearch[parentField]
        ? [payload.levels[lastLevelIndex - 1]]
        : [...subLevelSearch[parentField], payload.levels[lastLevelIndex - 1]];
    }
  } else {
    let shouldAddSearchId = true;
    if (lastLevelIndex < resource.lvSearchField.length - 1) {
      // couldn't add parents id if selected child of them
      const childFields = resource.lvSearchField.slice(lastLevelIndex - 1);

      childFields.forEach((f: string) => {
        if (subLevelSearch[f]) {
          const children = resource.children.find(
            (c: { name: string; id: string }) => c.id === lastLevelId
          );
          if (
            children && children.children &&
            children.children.find((c: { name: string; id: string }) =>
              subLevelSearch[f].includes(c.id)
            )
          ) {
            shouldAddSearchId = false;
          }
        }
      });
    }

    if (shouldAddSearchId) {
      const oldSearch = state.subLevelSearch[searchField];
      if (!oldSearch) {
        subLevelSearch[searchField] = [lastLevelId];
      } else if (!oldSearch.includes(lastLevelId)) {
        if (searchField == "categoryLv2Ids") {
          subLevelSearch[searchField] = [...oldSearch, lastLevelId];
        } else {
          subLevelSearch[searchField] = [lastLevelId];
        }
      }

      if (lastLevelIndex === resource.lvSearchField.length - 1) {
        // remove parents id if select the deepest level
        const removeFields = resource.lvSearchField.slice(0, lastLevelIndex);

        removeFields.forEach((f: string, i: number) => {
          if (subLevelSearch[f]) {
            subLevelSearch[f] = subLevelSearch[f].filter(
              (id: string) => id !== payload.levels[i]
            );

            if (subLevelSearch[f]) {
              subLevelSearch[f] = undefined;
            }
          }
        });
      }
    }
  }

  return {
    ...state,
    subLevel: updateSubLevelChecked(state.subLevel, payload.levels),
    subLevelSearch
  };
};

const updateSubLevelChecked = (
  subLevel: subLevelInterface[],
  levels: string[]
) => {
  const lastLevel = levels[levels.length - 1];

  return subLevel.map((sl: subLevelInterface) => {
    let { checked } = sl;

    if (checked === undefined) {
      checked = false;
    } else if (sl.id === lastLevel) {
      checked = !sl.checked;
    }

    return { ...sl, checked };
  });
};

const setSubLevel = (state: adInterface, { payload }) => {
  if (!payload) {
    return state;
  }

  const subLevel = payload.subLevel.map((l: subLevelInterface) => {
    const level = l;
    Object.values(state.subLevelSearch).forEach((ids: string[]) => {
      if (ids) {
        if (ids.includes(level.id)) {
          level.checked = true;
        }

        // checked (of parent level) should be set true if it has child in subLevelSearch
        if (
          payload.children[l.id] &&
          // eslint-disable-next-line max-nested-callbacks
          payload.children[level.id].find((id: string) => ids.includes(id))
        ) {
          level.checked = true;
        }
      }
    });

    return level;
  });

  return {
    ...state,
    subLevel,
    subLevelName: payload.levelName,
    subLevelSource: payload.dataSource,
    subLevelTree: payload.tree || [],
    subLevelSearchFields: payload.searchFields || [],
    subLevelParentCount: payload.parentCount || 0
  };
};

const removeSubLevelSearch = (state: adInterface, { payload }) => {
  if (!payload.levelId) {
    return {
      ...state,
      subLevel: [],
      subLevelSearch: {}
    };
  }

  if (!checkEmptyArray(state.subLevelSearchFields)) {
    return {
      ...state,
      subLevelSearch: {},
      subLevel: state.subLevel.map((l: subLevelInterface) =>
        reset(l, "checked", false)
      )
    };
  }

  const subLevelSearch = buildSubLevelSearch(state, payload);
  const existedLevel = state.subLevel.find(
    (sl: subLevelInterface) => sl.id === payload.levelId
  );

  const reset = (object, field, value) => {
    let result = { ...object };

    result = { ...result, [field]: value };
    return result;
  };

  const subLevel = state.subLevel.map((sl: subLevelInterface) => {
    if (!existedLevel || existedLevel.id === sl.id) {
      return reset(sl, "checked", false);
    }

    return sl;
  });

  return {
    ...state,
    subLevelSearch,
    subLevel
  };
};

// eslint-disable-next-line complexity
const buildSubLevelSearch = (
  state: adInterface,
  payload: BuildSubLevelPayloadI
) => {
  const resource = currentSubLevelResource(state);
  let subLevelSearch = { ...state.subLevelSearch };

  for (let index = 0; index < resource.lvSearchField.length; index += 1) {
    const field = resource.lvSearchField[index];
    if (index === 0) {
      // index = 0 mean checking search field level 1
      if (!subLevelSearch[field]) {
        subLevelSearch[field] = [payload.levelId];
      } else if (!subLevelSearch[field].includes(payload.levelId)) {
        subLevelSearch[field].push(payload.levelId);
      } else {
        subLevelSearch[field] = subLevelSearch[field].filter(
          (id: string) => payload.levelId !== id
        );
      }
    } else if (subLevelSearch[field]) {
      const { children } = resource.children.find(
        (c: SubLevelResourceChildrenI) => c.id === payload.levelId || c.route === payload.levelId
      );
      if (children) {
        subLevelSearch[field] = subLevelSearch[field].filter(
          (id: string) =>
            !children.find((c: { id: string; name: string }) => c.id === id)
        );
      }
    }

    if (subLevelSearch[field] && subLevelSearch[field].length === 0) {
      subLevelSearch = removeProperties(subLevelSearch, field);
    }
  }

  return subLevelSearch;
};

const setNewAds = (state: adInterface, { payload }) => {
  if (!payload) {
    return state;
  }

  let data;
  if (!state.newAds || !state.newAds.data || payload.page === 1) {
    data = [...payload.data];
  } else {
    const existingIds = new Set(state.newAds.data.map((ad: any) => ad.id));
    const newItems = payload.data.filter((ad: any) => !existingIds.has(ad.id));
    data = [...state.newAds.data, ...newItems];
  }

  return {
    ...state,
    newAds: {
      ...payload,
      data
    }
  };
};

const setDiscountedAds = (state: adInterface, { payload }) => {
  if (!payload) {
    return state;
  }

  const data =
    !state.discountedAds || !state.discountedAds.data || payload.page === 1
      ? [...payload.data]
      : [...state.discountedAds.data, ...payload.data];

  return {
    ...state,
    discountedAds: {
      ...payload,
      data
    }
  };
};

export default handleActions(
  {
    [AD_SECTION_CONFIGS_GET]: (state: adInterface, { payload }) => ({
      ...state,
      sectionConfigs: payload
    }),
    [AD_DETAILS_GET]: (state: adInterface, { payload }) => ({
      ...state,
      adDetails:
        state.adDetails && state.adDetails.creator
          ? { ...payload, creator: state.adDetails.creator }
          : { ...payload }
    }),
    [USER_GET_INFO]: (state: adInterface, { payload }) => ({
      ...state,
      adDetails: { ...state.adDetails, creator: payload }
    }),
    [AD_MY_ADS_GET]: (state: adInterface, { payload }) => ({
      ...state,
      myAds: checkEmptyObject(payload)
        ? {
          ...payload,
          data: [...payload.data]
        }
        : {}
    }),
    [AD_MY_ADS_STATISTIC]: (state: adInterface, { payload }) => ({
      ...state,
      myAdsStatistic: { ...payload }
    }),

    [AD_ENABLE_AUTO_PROMOTION]: (state: adInterface, { payload }) => ({
      ...state,
      myAds: { ...payload }
    }),

    [AD_DISABLE_AUTO_PROMOTION]: (state: adInterface, { payload }) => ({
      ...state,
      myAds: { ...payload }
    }),

    [AD_MY_ADS_DELETE]: (state: adInterface, { payload }) => ({
      ...state,
      myAds: { ...payload }
    }),
    [AD_MY_ADS_ACTIVE]: (state: adInterface, { payload }) => ({
      ...state,
      myAds: { ...payload }
    }),
    [AD_MY_ADS_RENEW]: (state: adInterface, { payload }) => ({
      ...state,
      myAds: { ...payload }
    }),
    [AD_MY_ADS_PAUSED]: (state: adInterface, { payload }) => ({
      ...state,
      myAds: { ...payload }
    }),
    [AD_MY_ADS_SOLD]: (state: adInterface, { payload }) => ({
      ...state,
      myAds: { ...payload }
    }),
    [AD_MY_ADS_PRICE_CHANGE]: (state: adInterface, { payload }) => ({
      ...state,
      myAds: { ...payload }
    }),
    [AD_STATISTICS_GET]: (state: adInterface, { payload }) => ({
      ...state,
      adStatistics: payload
    }),
    [AD_CAROUSEL_SCROLL_TRACK]: (state: adInterface, { payload }) => (
      state.trackedCarouselAdIds && state.trackedCarouselAdIds.indexOf(payload) !== -1
        ? state
        : { ...state, trackedCarouselAdIds: [...(state.trackedCarouselAdIds || []), payload] }
    ),
    [SEARCH_SUGGESTION_GET]: (state: adInterface, { payload }) => ({
      ...state,
      suggestions: payload
    }),
    [SEARCH_TEXT_SET]: (state: adInterface, { payload }) => ({
      ...state,
      searchText: payload
    }),
    [AD_NEW_GET]: setNewAds,
    [AD_NEW_CLEAR]: (state: adInterface) => ({
      ...state,
      newAds: {}
    }),
    [AD_DISCOUNTED_GET]: setDiscountedAds,
    [AD_DISCOUNTED_CLEAR]: (state: adInterface) => ({
      ...state,
      discountedAds: {}
    }),
    [POPULAR_GET]: (state: adInterface, { payload }) => ({
      ...state,
      populars: [...payload]
    }),
    [STATISTIC_GET]: (state: adInterface, { payload }) => ({
      ...state,
      statistics: payload
    }),
    [AD_SMARTLINK_GET]: (state: adInterface, { payload }) => ({
      ...state,
      smartlinkData: payload
    }),
    [SIMILAR_GET]: (state: adInterface, { payload }) => ({
      ...state,
      similarAds: payload
    }),
    [SELLER_MODULE_GET]: (state: adInterface, { payload }) => ({
      ...state,
      seller: { ...state.seller, modules: payload }
    }),
    [SUB_LEVEL_GET]: setSubLevel,
    [SUB_LEVEL_SEARCH_UPDATE]: setSubLevelSearch,
    [SUB_LEVEL_SEARCH_DELETE]: removeSubLevelSearch,
    [AD_SET_FORBID_ADS]: (state: adInterface, { payload }) => ({
      ...state,
      forbiddAds: payload
    }),
    [AD_SET_AGE_VERIFIED]: (state: adInterface, { payload }) => ({
      ...state,
      isAgeVerified: payload
    }),
  },
  initialState
);



// WEBPACK FOOTER //
// ./src/routes/Ad/ad.ducks.ts